import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import {
  useGetEmailTemplateById,
  useUpdateEmailTemplate,
} from '@nxsys/hooks/api/company/useCompanyApi';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateEmailTemplateParams } from '@nxsys/types/api/company';
import { showMessage } from '@nxsys/utils/message';
import { Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import EmailTemplateForm, {
  EmailTemplateFormRef,
  EmailTemplateFormValues,
} from './components/EmailTemplateForm';

const EmailTemplateEdit = () => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const companyId = params.companyId ?? '';
  const templateId = params.emailTemplateId ?? '';

  const formRef = useRef<EmailTemplateFormRef>(null);

  const {
    run: fetchEmailTemplate,
    loading: isFetching,
    value: emailTemplateValue,
  } = useGetEmailTemplateById();
  const data = useMemo(() => emailTemplateValue?.data, [emailTemplateValue]);

  const { run: updateEmailTemplate, loading: isUpdating } =
    useUpdateEmailTemplate();

  useEffect(() => {
    if (templateId) fetchEmailTemplate(templateId);
  }, [templateId]);

  const { goBack } = useGoBackByQuery();

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: companyId ? `/companies/${companyId}` : '/companies',
    });

  const renderTitle = useCallback(
    () => (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          separator=""
          prefix={messages['company.emailTemplate.editTitle']}
          title=""
        />
      </div>
    ),
    [messages],
  );

  const renderRightContent = useCallback(
    () => (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel']}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save']}
            onClick={() => formRef.current?.submit()}
            loading={isUpdating}
            disabled={isUpdating}
          />
        </Space>
      </div>
    ),
    [messages, isUpdating],
  );

  const handleSubmit = async (values: EmailTemplateFormValues) => {
    if (!templateId || !companyId) return;

    const payload: UpdateEmailTemplateParams = {
      companyId,
      // The stored id: bureau shows self_bill as payslips, but it stays self_bill.
      templateId: data?.templateId ?? values.templateId ?? '',
      status: values.status?.value ?? 'ACTIVE',
      subject: values.subject ?? '',
      context: values.context ?? '',
      signature: values.signature ?? '',
    };

    const res = await updateEmailTemplate(templateId, payload);
    if (res?.success) {
      showMessage({
        type: 'success',
        content: messages['company.emailTemplate.updateSuccess'],
      });
      handleGoBack();
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isFetching}
    >
      {data && (
        <div className="px-6 py-4">
          <EmailTemplateForm
            formRef={formRef}
            onSubmit={handleSubmit}
            data={data}
          />
        </div>
      )}
    </AppsContainer>
  );
};

export default EmailTemplateEdit;
